import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft,
    Building2,
    Calendar,
    Download,
    FileText,
    Loader2,
    Sparkles,
    Trash2,
    Euro,
    Upload,
    Send,
} from "lucide-react";
import { Layout } from "@/components/Layout";
import { TourButton } from "@/components/TourButton";
import { useTour } from "@/hooks/useTour";
import { tenderDetailTour } from "@/tours";
import { PdfUploader } from "@/components/PdfUploader";
import { AnalysisView } from "@/components/AnalysisView";
import { SolvencyMatchCard } from "@/components/SolvencyMatchCard";
import { CostBreakdown } from "@/components/CostBreakdown";
import { AnnexGenerator } from "@/components/AnnexGenerator";
import { StatusBadge } from "@/components/StatusBadge";
import { OutcomePanel } from "@/components/OutcomePanel";
import { ChecklistView } from "@/components/ChecklistView";
import { TenderChat } from "@/components/TenderChat";
import DownloadPackageMenu from "@/components/tender/DownloadPackageMenu";
import MemoriaTecnica from "@/components/tender/MemoriaTecnica";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
    analyzeTender,
    deleteTender,
    fileUrl,
    generateTenderBundle,
    getTender,
    getTenderBundleStatus,
    getSolvencyProfile,
    retryPliegosHarvest,
    updateTender,
} from "@/lib/api";
import { daysUntil, fmtDate, fmtEUR, fmtDateTime } from "@/lib/format";

export default function TenderDetail() {
    const tour = useTour("tour:tender-detail", tenderDetailTour, { autoStart: false });
    const { id } = useParams();
    const navigate = useNavigate();
    const [tender, setTender] = useState(null);
    const [loading, setLoading] = useState(true);
    const [analyzing, setAnalyzing] = useState(false);
    const [tab, setTab] = useState("resumen");
    const [bundling, setBundling] = useState(false);
    const [bundleProgress, setBundleProgress] = useState(null); // {done, total}
    const [solvency, setSolvency] = useState(null); // {has_data, tecnica_count, economica_count}
    const [retryingHarvest, setRetryingHarvest] = useState(false);

    // Load solvency snapshot once so we can show contextually whether to attach.
    useEffect(() => {
        let cancelled = false;
        getSolvencyProfile()
            .then((s) => {
                if (!cancelled) setSolvency(s);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, []);

    const handleBundle = async () => {
        setBundling(true);
        setBundleProgress({ done: 0, total: 0 });
        try {
            const initial = await generateTenderBundle(id);
            setBundleProgress({ done: initial.done || 0, total: initial.total || 0 });
            if (!initial.total) {
                toast.success("Todos los anexos requeridos ya están generados");
                setBundling(false);
                return;
            }
            toast.info(`Generando ${initial.total} anexos con IA…`);
            // Poll status every 3s
            const startedAt = Date.now();
            while (true) {
                await new Promise((r) => setTimeout(r, 3000));
                let status;
                try {
                    status = await getTenderBundleStatus(id);
                } catch {
                    continue;
                }
                setBundleProgress({ done: status.done || 0, total: status.total || 0 });
                if (!status.running) break;
                if (Date.now() - startedAt > 8 * 60 * 1000) {
                    toast.error("La generación está tardando demasiado. Vuelve a intentarlo.");
                    break;
                }
            }
            const updated = await getTender(id);
            setTender(updated);
            toast.success(
                "Anexos generados. Ya puedes firmarlos y descargar el paquete."
            );
            setTab("anexos");
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                    "No se pudo preparar el paquete. Inténtalo de nuevo."
            );
        } finally {
            setBundling(false);
            setBundleProgress(null);
        }
    };

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const t = await getTender(id);
            setTender(t);
        } catch {
            toast.error("Licitación no encontrada");
            navigate("/app");
        } finally {
            setLoading(false);
        }
    }, [id, navigate, setTender, setLoading]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // Poll while analysis is running in background OR while the headless
    // harvester is fetching pliegos from an autonomic/local platform.
    useEffect(() => {
        if (!tender) return;
        const pendingPliegos =
            tender.pliegos_harvest_pending === true ||
            (tender.pliegos_pending_external &&
                (!tender.documents || tender.documents.length === 0));
        if (tender.status !== "analyzing" && !pendingPliegos) return;
        const interval = setInterval(async () => {
            try {
                const t = await getTender(id);
                setTender(t);
                const stillPending = t.pliegos_harvest_pending === true;
                if (t.status !== "analyzing" && !stillPending) {
                    clearInterval(interval);
                    if (t.status === "analyzed") {
                        toast.success("Análisis IA completado");
                        setTab("resumen");
                    } else if (t.analysis_error) {
                        toast.error(t.analysis_error);
                    } else if (t.documents?.length) {
                        toast.success(
                            `${t.documents.length} pliego(s) descargado(s) automáticamente`,
                        );
                    } else {
                        toast.warning(
                            "No se han encontrado pliegos públicos. Descárgalos de la plataforma oficial y súbelos manualmente.",
                            { duration: 8000 },
                        );
                    }
                }
            } catch (err) {
                console.warn("[tender-poll] transient error:", err?.message || err);
            }
        }, 5000);
        return () => clearInterval(interval);
    }, [tender, id]);

    const analyze = async () => {
        setAnalyzing(true);
        try {
            const t = await analyzeTender(id);
            setTender(t);
            if (t.status === "analyzing") {
                toast.info("Analizando con IA — esto puede tardar 1-2 minutos…");
            } else {
                toast.success("Análisis IA completado");
                setTab("resumen");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo analizar");
        } finally {
            setAnalyzing(false);
        }
    };

    const handleStatusChange = async (newStatus) => {
        try {
            const t = await updateTender(id, { status: newStatus });
            setTender(t);
            toast.success("Estado actualizado");
        } catch {
            toast.error("Error al actualizar");
        }
    };

    const handlePresentar = async () => {
        if (tender?.status === "submitted") {
            navigate("/app/crm");
            return;
        }
        try {
            const t = await updateTender(id, { status: "submitted" });
            setTender(t);
            toast.success(
                "Licitación presentada y movida al CRM",
                {
                    action: {
                        label: "Ver en CRM",
                        onClick: () => navigate("/app/crm"),
                    },
                    duration: 8000,
                },
            );
        } catch {
            toast.error("No se pudo marcar como presentada");
        }
    };

    const handleDelete = async () => {
        try {
            await deleteTender(id);
            toast.success("Licitación eliminada");
            navigate("/app");
        } catch {
            toast.error("Error al eliminar");
        }
    };

    const handleRetryHarvest = async () => {
        setRetryingHarvest(true);
        try {
            const res = await retryPliegosHarvest(id);
            if (res?.status === "already_has_documents") {
                toast.info("Esta licitación ya tiene pliegos descargados.");
                const t = await getTender(id);
                setTender(t);
            } else if (res?.status === "harvest_already_running") {
                toast.info("Ya hay una descarga en curso — espera unos segundos.");
            } else if (res?.status === "harvest_started") {
                toast.success(
                    "Reintentando descarga de pliegos en segundo plano…",
                );
                // Optimistically flip the flag so the existing poller wakes up.
                setTender({ ...tender, pliegos_harvest_pending: true });
            } else {
                toast.info("Solicitud enviada.");
            }
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                    "No se pudo reintentar la descarga.",
            );
        } finally {
            setRetryingHarvest(false);
        }
    };

    if (loading)
        return (
            <Layout>
                <div className="max-w-[1400px] mx-auto p-8 space-y-4">
                    <Skeleton className="h-8 w-72" />
                    <Skeleton className="h-32 w-full" />
                    <Skeleton className="h-96 w-full" />
                </div>
            </Layout>
        );

    if (!tender) return null;

    const d = daysUntil(tender.deadline);
    const hasPliego = (tender.documents || []).length > 0;

    return (
        <Layout>
            <div className="max-w-[1400px] mx-auto p-6 md:p-8" data-testid="tender-detail">
                <button
                    onClick={() => navigate("/app")}
                    className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4"
                    data-testid="back-to-dashboard"
                >
                    <ArrowLeft size={14} />
                    Panel de licitaciones
                </button>

                {/* Header card */}
                <div className="bg-white border border-slate-200 p-6 md:p-8 mb-6">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5">
                        <div className="min-w-0">
                            <div className="flex items-center gap-3 mb-2">
                                <StatusBadge status={tender.status} />
                                {tender.reference && (
                                    <span className="font-mono text-xs text-slate-500">
                                        {tender.reference}
                                    </span>
                                )}
                            </div>
                            <h1 className="font-display text-2xl md:text-3xl tracking-tight font-medium text-slate-950 mb-3">
                                {tender.title}
                            </h1>
                            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-600">
                                {tender.contracting_authority && (
                                    <span className="inline-flex items-center gap-1.5">
                                        <Building2 size={13} className="text-slate-400" />
                                        {tender.contracting_authority}
                                    </span>
                                )}
                                {tender.deadline && (
                                    <span className="inline-flex items-center gap-1.5">
                                        <Calendar size={13} className="text-slate-400" />
                                        {fmtDate(tender.deadline)}
                                        {d !== null && d >= 0 && (
                                            <span
                                                className={`ml-1 font-mono text-xs px-1.5 py-0.5 rounded-xl ${
                                                    d < 7
                                                        ? "bg-red-50 text-red-700 border border-red-200"
                                                        : d < 30
                                                          ? "bg-amber-50 text-amber-800 border border-amber-200"
                                                          : "bg-slate-50 border border-slate-200 text-slate-600"
                                                }`}
                                            >
                                                {d === 0 ? "hoy" : `${d}d`}
                                            </span>
                                        )}
                                    </span>
                                )}
                                {tender.budget != null && (
                                    <span className="inline-flex items-center gap-1.5 font-mono">
                                        <Euro size={13} className="text-slate-400" />
                                        {fmtEUR(tender.budget)}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2 shrink-0">
                            <select
                                value={tender.status}
                                onChange={(e) => handleStatusChange(e.target.value)}
                                className="rounded-xl border border-slate-300 bg-white px-3 h-9 text-sm"
                                data-testid="status-selector"
                            >
                                <option value="draft">Borrador</option>
                                <option value="analyzed">Analizada</option>
                                <option value="in_progress">En preparación</option>
                                <option value="submitted">Enviada</option>
                                <option value="archived">Archivada</option>
                            </select>
                            <Button
                                onClick={handlePresentar}
                                className={`rounded-xl h-9 font-bold gap-1.5 shadow-pop text-white ${
                                    tender.status === "submitted"
                                        ? "bg-emerald-600 hover:bg-emerald-700"
                                        : "bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                                }`}
                                title={
                                    tender.status === "submitted"
                                        ? "Esta licitación ya está en el CRM"
                                        : "Marca la licitación como presentada y la añade al CRM"
                                }
                                data-testid="presentar-licitacion-btn"
                            >
                                <Send size={14} />
                                {tender.status === "submitted"
                                    ? "Ver en CRM"
                                    : "Presentar licitación"}
                            </Button>
                            <Button
                                onClick={handleBundle}
                                disabled={bundling || !tender?.checklist?.length}
                                className="rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white h-9 font-bold gap-1.5 shadow-pop disabled:opacity-50"
                                title={
                                    tender?.checklist?.length
                                        ? "Genera todos los anexos obligatorios del pliego con IA"
                                        : "Espera a que se genere la checklist"
                                }
                                data-testid="prepare-bundle-btn"
                            >
                                {bundling ? (
                                    <>
                                        <Loader2 size={14} className="animate-spin" />
                                        {bundleProgress?.total
                                            ? `Generando ${bundleProgress.done}/${bundleProgress.total}…`
                                            : "Preparando…"}
                                    </>
                                ) : (
                                    <>
                                        <Sparkles size={14} />
                                        Preparar paquete IA
                                    </>
                                )}
                            </Button>
                            <DownloadPackageMenu tenderId={id} solvency={solvency} />
                            <AlertDialog>
                                <AlertDialogTrigger asChild>
                                    <Button
                                        variant="outline"
                                        className="rounded-xl border-slate-300 text-red-600 h-9"
                                        data-testid="delete-tender-btn"
                                    >
                                        <Trash2 size={14} />
                                    </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent className="rounded-xl">
                                    <AlertDialogHeader>
                                        <AlertDialogTitle>¿Eliminar licitación?</AlertDialogTitle>
                                        <AlertDialogDescription>
                                            Esta acción no se puede deshacer. Se eliminarán el
                                            análisis, escandallo y anexos asociados.
                                        </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                        <AlertDialogCancel className="rounded-xl">
                                            Cancelar
                                        </AlertDialogCancel>
                                        <AlertDialogAction
                                            onClick={handleDelete}
                                            className="rounded-xl bg-red-600 hover:bg-red-700"
                                        >
                                            Eliminar
                                        </AlertDialogAction>
                                    </AlertDialogFooter>
                                </AlertDialogContent>
                            </AlertDialog>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <Tabs value={tab} onValueChange={setTab} className="w-full">
                    <TabsList className="bg-transparent border-b border-slate-200 rounded-none h-auto p-0 mb-6 w-full justify-start overflow-x-auto">
                        {[
                            ["resumen", "Resumen IA"],
                            ["checklist", "Checklist"],
                            ["escandallo", "Escandallo"],
                            ["memoria", "Memoria técnica"],
                            ["anexos", "Anexos"],
                            ["documentos", "Documentos"],
                            ["resultado", "Resultado"],
                        ].map(([key, label]) => (
                            <TabsTrigger
                                key={key}
                                value={key}
                                className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0055ff] data-[state=active]:text-[#0055ff] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-3 text-sm font-medium text-slate-500"
                                data-testid={`tab-${key}`}
                            >
                                {label}
                            </TabsTrigger>
                        ))}
                    </TabsList>

                    {/* Summary / Analysis */}
                    <TabsContent value="resumen" className="mt-0 space-y-5">
                        {!hasPliego && (
                            <PdfUploader tenderId={id} onUploaded={setTender} />
                        )}

                        {hasPliego && !tender.analysis && (
                            <div className="bg-white border border-slate-200 p-8 text-center">
                                <div className="w-12 h-12 mx-auto bg-[#eff5ff] text-[#0055ff] border border-sky-100 flex items-center justify-center rounded-xl mb-4">
                                    <Sparkles size={18} />
                                </div>
                                <div className="font-display text-xl font-medium mb-2">
                                    Pliego cargado, listo para analizar
                                </div>
                                <div className="text-sm text-slate-500 mb-5 max-w-md mx-auto">
                                    La IA extraerá objeto, presupuesto, plazos, criterios de
                                    valoración, requisitos técnicos y riesgos en menos de un
                                    minuto.
                                </div>
                                <Button
                                    onClick={analyze}
                                    disabled={analyzing}
                                    size="lg"
                                    className="rounded-xl bg-[#0055ff] hover:bg-[#0044cc] text-white"
                                    data-testid="analyze-btn"
                                >
                                    {analyzing ? (
                                        <>
                                            <Loader2
                                                size={16}
                                                className="mr-2 animate-spin"
                                            />
                                            Analizando con Claude…
                                        </>
                                    ) : (
                                        <>
                                            <Sparkles size={16} className="mr-2" />
                                            Analizar con IA
                                        </>
                                    )}
                                </Button>
                            </div>
                        )}

                        {tender.analysis && (
                            <>
                                <TenderChat tender={tender} onUpdated={setTender} />
                                <div className="flex justify-end">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="rounded-xl border-slate-300"
                                        onClick={analyze}
                                        disabled={analyzing}
                                        data-testid="reanalyze-btn"
                                    >
                                        {analyzing ? (
                                            <>
                                                <Loader2
                                                    size={13}
                                                    className="mr-1.5 animate-spin"
                                                />
                                                Reanalizando…
                                            </>
                                        ) : (
                                            <>
                                                <Sparkles size={13} className="mr-1.5" />
                                                Volver a analizar
                                            </>
                                        )}
                                    </Button>
                                </div>
                                <AnalysisView analysis={tender.analysis} />
                                <SolvencyMatchCard tenderId={id} tender={tender} />
                            </>
                        )}
                    </TabsContent>

                    <TabsContent value="escandallo" className="mt-0">
                        <CostBreakdown tender={tender} onChange={setTender} />
                    </TabsContent>

                    <TabsContent value="checklist" className="mt-0">
                        <ChecklistView tender={tender} onChange={setTender} />
                    </TabsContent>

                    <TabsContent value="memoria" className="mt-0">
                        <MemoriaTecnica
                            tenderId={id}
                            hasPliego={hasPliego}
                        />
                    </TabsContent>

                    <TabsContent value="anexos" className="mt-0">
                        <AnnexGenerator tender={tender} onChange={setTender} />
                    </TabsContent>

                    <TabsContent value="resultado" className="mt-0">
                        <OutcomePanel tender={tender} onChange={setTender} />
                    </TabsContent>

                    <TabsContent value="documentos" className="mt-0 space-y-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="font-display text-2xl font-medium">
                                    Documentos originales
                                </h2>
                                <p className="text-sm text-slate-500 mt-1">
                                    Pliegos y documentación subida para el análisis.
                                </p>
                            </div>
                        </div>
                        {!hasPliego &&
                            tender.plcsp_link &&
                            !tender.pliegos_harvest_pending && (
                                <div
                                    data-testid="pliegos-missing-card"
                                    className="rounded-2xl border border-amber-200 bg-amber-50/50 p-5 flex flex-col md:flex-row md:items-center gap-4"
                                >
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-amber-900">
                                            No se han descargado pliegos
                                            automáticamente
                                        </p>
                                        <p className="text-xs text-amber-700 mt-1 leading-relaxed">
                                            Esta plataforma autonómica requiere
                                            navegación interactiva. Puedes
                                            reintentar la descarga, abrir la
                                            ficha original para descargar los
                                            PDFs a mano, o arrastrarlos al
                                            recuadro de abajo (la IA los
                                            analizará automáticamente).
                                        </p>
                                    </div>
                                    <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            data-testid="open-source-link-btn"
                                            className="rounded-xl border-amber-300 bg-white"
                                            onClick={() =>
                                                window.open(
                                                    tender.plcsp_link,
                                                    "_blank",
                                                    "noreferrer",
                                                )
                                            }
                                        >
                                            Abrir plataforma origen
                                        </Button>
                                        <Button
                                            size="sm"
                                            data-testid="retry-pliegos-harvest-btn"
                                            disabled={retryingHarvest}
                                            className="rounded-xl bg-[#0055ff] hover:bg-[#0044cc] text-white"
                                            onClick={handleRetryHarvest}
                                        >
                                            {retryingHarvest
                                                ? "Lanzando…"
                                                : "Reintentar descarga"}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        {tender.pliegos_harvest_pending && !hasPliego && (
                            <div
                                data-testid="pliegos-harvest-running-card"
                                className="rounded-2xl border border-sky-200 bg-sky-50/50 p-5 text-sm text-sky-900"
                            >
                                <span className="font-medium">
                                    Buscando pliegos en la plataforma origen…
                                </span>{" "}
                                el análisis IA arrancará automáticamente cuando
                                los encuentre (≈ 30-60 s).
                            </div>
                        )}
                        <PdfUploader tenderId={id} onUploaded={setTender} />
                        <div className="bg-white border border-slate-200">
                            {!(tender.documents || []).length ? (
                                <div className="py-10 text-center text-sm text-slate-400">
                                    Sin documentos subidos.
                                </div>
                            ) : (
                                <div className="divide-y divide-slate-100">
                                    {tender.documents.map((d) => (
                                        <div
                                            key={d.id}
                                            className="p-4 flex items-center justify-between gap-3"
                                        >
                                            <div className="flex gap-3 min-w-0">
                                                <div className="w-9 h-9 bg-[#0055ff]/5 text-[#0055ff] flex items-center justify-center rounded-xl shrink-0">
                                                    <FileText size={14} />
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="font-medium text-sm text-slate-900 truncate">
                                                        {d.original_filename}
                                                    </div>
                                                    <div className="text-xs text-slate-500 mt-0.5">
                                                        {fmtDateTime(d.uploaded_at)} · {(d.size / 1024).toFixed(0)} KB
                                                    </div>
                                                </div>
                                            </div>
                                            <a
                                                href={fileUrl(d.storage_path)}
                                                target="_blank"
                                                rel="noreferrer"
                                                download
                                            >
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="rounded-xl border-slate-300"
                                                >
                                                    <Download size={13} className="mr-1" />
                                                    Descargar
                                                </Button>
                                            </a>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </TabsContent>
                </Tabs>
            </div>
            <TourButton onClick={tour.start} label="Ver tour" />
        </Layout>
    );
}
